import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, filter, Subject, switchMap, take, throwError } from "rxjs";
import { ApiService } from "../../api/services/api.service";

let isRefreshing = false;
const refreshSubject = new Subject<boolean>();

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    const router = inject(Router);
    const apiService = inject(ApiService);

    // Don't intercept the refresh call itself
    if (request.url.includes('/session/refresh')) {
        return next(request);
    }

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                return throwError(() => error);
            }

            const refreshToken = localStorage.getItem("refreshToken");

            if (!refreshToken) {
                return handleLogout(router);
            }

            if (isRefreshing) {
                return refreshSubject.pipe(
                    filter((success) => success !== null),
                    take(1),
                    switchMap((success) => {
                        if (success) {
                            return next(addTokenToRequest(request));
                        }
                        return throwError(() => error);
                    }),
                );
            }

            isRefreshing = true;

            return apiService.refreshSession({ refreshToken }).pipe(
                switchMap((newUserData) => {
                    isRefreshing = false;
                    localStorage.setItem("token", newUserData.token);
                    localStorage.setItem("refreshToken", newUserData.refreshToken || "");
                    localStorage.setItem("userInfo", JSON.stringify(newUserData));
                    refreshSubject.next(true);
                    return next(addTokenToRequest(request));
                }),
                catchError(() => {
                    isRefreshing = false;
                    refreshSubject.next(false);
                    return handleLogout(router);
                }),
            );
        }),
    );
};

function addTokenToRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = localStorage.getItem("token");
    return request.clone({
        setHeaders: { "x-auth-session": token || "" },
    });
}

function handleLogout(router: Router) {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userInfo");
    router.navigate(["/login"]);
    return throwError(() => new HttpErrorResponse({ status: 401, statusText: "Session expired" }));
}
